import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import offlineManager from '../../utils/offlineManager';

export default function MobileOrderSearch() {
    const { user } = useAuth();
    const { showToast } = useUI();
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('Tous'); // Tous, En cours, Clôturées

    const fetchOrders = useCallback(async () => {
        if (!user?.companyId) return;
        setLoading(true);
        try {
            let processedOrders = [];

            if (navigator.onLine) {
                const queryParams = new URLSearchParams({
                    companyId: user.companyId,
                    search: search,
                    limit: 50,
                    sortBy: 'createdAt',
                    sortOrder: 'DESC'
                });

                if (statusFilter !== 'Tous') {
                    queryParams.append('status', statusFilter);
                }

                const res = await fetch(`/api/purchase-orders?${queryParams.toString()}`);
                if (res.ok) {
                    const data = await res.json();
                    processedOrders = data.orders;
                    // Cache the results for offline search
                    await offlineManager.saveOrders(data.orders);
                }
            } else {
                // FALLBACK OFFLINE SEARCH
                processedOrders = await offlineManager.searchOrders(search, user.companyId, statusFilter);
                showToast("Mode hors-ligne : recherche locale", "info");
            }

            // FILTERING REFINEMENT: If no search + default filter, show only active ones
            // (Note: searchOrders already does it, but we apply it here for the Online results too)
            if (!search && statusFilter === 'Tous') {
                processedOrders = processedOrders.filter(o => ['Envoyée', 'Livrée', 'Partiellement Livrée'].includes(o.status));
            }

            // Get offline receptions to calculate real-time progress
            const queuedReceptions = await offlineManager.getQueuedReceptions();

            const finalizedOrders = processedOrders.map(o => {
                const pendingForThisOrder = queuedReceptions.filter(r => r.orderId === o.id);

                const totalOrdered = Number(o.totalQty || 0);
                const serverReceived = Number(o.receivedQty || 0);

                const offlineDeltas = pendingForThisOrder.reduce((s, r) =>
                    s + (r.items || []).reduce((si, item) => si + (Number(item.delta) || 0), 0), 0
                );

                const currentTotalReceived = serverReceived + offlineDeltas;
                const remaining = Math.max(0, totalOrdered - currentTotalReceived);
                const progress = totalOrdered > 0 ? (currentTotalReceived / totalOrdered) * 100 : 0;

                // Dynamic Status Calculation for Offline Consistency
                let effectiveStatus = o.status;
                if (offlineDeltas !== 0) {
                    if (currentTotalReceived >= totalOrdered) {
                        effectiveStatus = 'Clôturée';
                    } else if (currentTotalReceived > 0) {
                        effectiveStatus = 'Partiellement Livrée';
                    } else {
                        effectiveStatus = 'Envoyée'; // Or 'Livrée' depending on original state, but 'Envoyée' is safe
                    }
                }

                return {
                    ...o,
                    currentTotalReceived,
                    remaining,
                    progress,
                    effectiveStatus,
                    isOverloaded: currentTotalReceived > totalOrdered
                };
            });

            // Re-apply status filter if we have dynamic status
            let finalResults = finalizedOrders;
            if (statusFilter !== 'Tous') {
                finalResults = finalizedOrders.filter(o => o.effectiveStatus === statusFilter);
            }

            setOrders(finalResults);
        } catch (err) {
            console.error("Search failed", err);
            showToast("Erreur lors de la recherche", "error");
        } finally {
            setLoading(false);
        }
    }, [user?.companyId, search, statusFilter, showToast]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchOrders();
        }, 300);
        return () => clearTimeout(timer);
    }, [fetchOrders]);

    return (
        <div className="space-y-6">
            <header className="flex flex-col gap-4">
                <h1 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">search</span>
                    Recherche Commandes
                </h1>

                {/* Search Input */}
                <div className="relative">
                    <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">search</span>
                    <input
                        type="text"
                        placeholder="Référence ou Fournisseur..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm focus:ring-2 focus:ring-primary outline-none transition-all"
                    />
                </div>

                {/* Filter Chips */}
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    {['Tous', 'Envoyée', 'Partiellement Livrée', 'Clôturée'].map(status => (
                        <button
                            key={status}
                            onClick={() => setStatusFilter(status)}
                            className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all ${statusFilter === status
                                ? 'bg-primary text-white shadow-lg shadow-primary/20'
                                : 'bg-white dark:bg-slate-800 text-slate-500 border border-slate-200 dark:border-slate-700'
                                }`}
                        >
                            {status === 'Tous' ? 'Toutes' : status}
                        </button>
                    ))}
                </div>
            </header>

            {/* Results */}
            <section className="space-y-3">
                {loading && orders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                        <p>Chargement des commandes...</p>
                    </div>
                ) : orders.length === 0 ? (
                    <div className="p-12 text-center bg-slate-50 dark:bg-slate-800/50 rounded-3xl border border-dashed border-slate-200 dark:border-slate-700">
                        <span className="material-symbols-outlined text-5xl text-slate-300 mb-4">inventory_2</span>
                        <p className="text-slate-500 font-medium">Aucune commande trouvée</p>
                    </div>
                ) : (
                    <div className="grid gap-3">
                        {orders.map(order => (
                            <Link
                                to={`/mobile/orders/${order.id}`}
                                key={order.id}
                                className="block p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm active:scale-[0.98] transition-all"
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex flex-col">
                                        <span className="font-black text-slate-900 dark:text-white">{order.reference}</span>
                                        <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">{order.supplier?.name}</span>
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        {order.isOverloaded && (
                                            <span className="text-[9px] font-black px-2 py-1 rounded bg-rose-500 text-white animate-pulse">
                                                SURPLUS
                                            </span>
                                        )}
                                        <span className={`text-[10px] font-black px-2 py-1 rounded-lg ${order.effectiveStatus === 'Clôturée'
                                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                            }`}>
                                            {order.effectiveStatus}
                                        </span>
                                    </div>
                                </div>

                                {/* Progress Bar */}
                                <div className="space-y-2">
                                    <div className="flex justify-between items-end text-[9px] font-black uppercase tracking-widest text-slate-400">
                                        <span>Progression</span>
                                        <span className="text-primary">{order.currentTotalReceived} / {order.totalQty}</span>
                                    </div>
                                    <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all duration-500 ${order.effectiveStatus === 'Clôturée' ? 'bg-emerald-500' : 'bg-primary'}`}
                                            style={{ width: `${order.progress}%` }}
                                        ></div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-50 dark:border-slate-700/50">
                                    <div className="flex items-center gap-2 text-[10px] text-slate-500 font-bold">
                                        <span className="material-symbols-outlined text-[14px]">calendar_today</span>
                                        {new Date(order.createdAt).toLocaleDateString()}
                                    </div>
                                    <span className="material-symbols-outlined text-slate-300">chevron_right</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
